/**
 * Inbound Media — tenant-wide browser for files customers have sent.
 *
 * Deliberately a companion to the Media tab inside a conversation, not a
 * replacement: media is usually understood in thread context. This view exists
 * for the cross-conversation questions a thread cannot answer — "what did we
 * receive this week", "find that invoice", "clear out last year".
 *
 * Access is gated on `activity.media`, which is stricter than chat access:
 * read grants thumbnails, edit grants the full-size original, full grants
 * deletion. The server issues presigned URLs accordingly, so a read-only agent
 * simply never receives a link to the original.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  CloudOffOutlined,
  DeleteOutline,
  DescriptionOutlined,
  GraphicEqOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  OpenInNew,
  PermMedia,
  Refresh,
  VideocamOutlined,
} from '@mui/icons-material';
import UsageChip from '@/components/common/UsageChip';
import MediaPreviewDialog, { type MediaPreviewKind } from '@/components/common/MediaPreviewDialog';
import { refreshUsage, patchUsage, useUsageLimits } from '@/hooks/useUsageLimits';
import { useWebSocket, type MediaQuotaEvent } from '@/hooks/useWebSocket';
import { useAuth } from '@/hooks/useAuth';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import adminApi from '@/services/admin.api';

interface MediaItem {
  messageId: string;
  attachmentIndex: number;
  conversationId: string;
  customerPhone?: string;
  customerName?: string;
  messageType?: string;
  mimeType?: string;
  size: number;
  /** Over-quota capture: what freeing space would recover. `size` is 0 for
   *  these — it counts bytes actually held. */
  skippedBytes?: number;
  storageState?: string;
  /** Why a queued download has not landed — set for non-transient failures. */
  error?: string;
  createdAt: string;
  expiresAt?: string;
  promoted: boolean;
  legalHold: boolean;
  thumbUrl?: string;
  s3Url?: string;
}

const TYPE_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All types' },
  { value: 'image,sticker', label: 'Images' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Voice & audio' },
  { value: 'document', label: 'Documents' },
];

const RETENTION_WINDOWS = [30, 90, 180, 365];

function typeIcon(t?: string) {
  switch (t) {
    case 'image':
    case 'sticker':
      return <ImageOutlined fontSize="small" />;
    case 'video':
      return <VideocamOutlined fontSize="small" />;
    case 'audio':
      return <GraphicEqOutlined fontSize="small" />;
    case 'document':
      return <DescriptionOutlined fontSize="small" />;
    default:
      return <InsertDriveFileOutlined fontSize="small" />;
  }
}

/** The dialog's `document` kind means "render as a PDF", so it is only claimed
 *  when the mime type says so — a .docx must fall through to the honest
 *  "preview not available" tile rather than an empty frame. */
function previewKind(t?: string): MediaPreviewKind | undefined {
  switch (t) {
    case 'image':
    case 'sticker':
      return 'image';
    case 'video':
      return 'video';
    case 'audio':
      return 'audio';
    default:
      return undefined;
  }
}

function formatBytes(n: number): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Human label for an attachment whose bytes are not (or no longer) available.
 *  A queued download carrying an `error` has stalled on something a human has
 *  to fix (usually a rejected WhatsApp token) — the tile is too small for the
 *  full reason, so it says so briefly and the chat bubble spells it out. */
function unavailableLabel(state?: string, error?: string): string | null {
  switch (state) {
    case 'pending':
      return error ? 'Download blocked' : 'Downloading';
    case 'expired':
      return 'No longer available';
    case 'skipped_quota':
      return 'Not stored';
    case 'failed':
      return 'Download failed';
    case 'deleted':
      return 'Removed';
    default:
      return null;
  }
}

export default function InboundMedia() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [types, setTypes] = useState('');
  // Which channel's inbound-media store this page is browsing. Both channels
  // share one retention/quota pool server-side (Tenant.usage.
  // conversationMediaUsed has no per-channel split), but each has its OWN
  // message collection to browse — see conversation-media-deletion.service.ts's
  // ConversationMediaChannel doc comment for why 'instagram' needed its own
  // explicit param rather than being merged into one query.
  const [channel, setChannel] = useState<'whatsapp' | 'instagram'>('whatsapp');
  const [canOpenOriginal, setCanOpenOriginal] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweepDays, setSweepDays] = useState(365);
  const [sweepPreview, setSweepPreview] = useState<{ count: number; bytes: number } | null>(null);
  const [sweeping, setSweeping] = useState(false);
  const [error, setError] = useState('');
  /** The item whose original is open in the in-app preview. Gated exactly like
   *  the old "Open original" link was — only ever set for an item that has an
   *  `s3Url` AND a caller allowed to see originals. */
  const [preview, setPreview] = useState<MediaItem | null>(null);

  // Guards against a slow first page landing after a newer filter's response.
  const requestSeq = useRef(0);

  const load = useCallback(
    async (opts: { append?: boolean; before?: string; typeFilter?: string } = {}) => {
      const seq = ++requestSeq.current;
      opts.append ? setLoadingMore(true) : setLoading(true);
      setError('');
      try {
        const res = await adminApi.listInboundMedia({
          ...(opts.typeFilter ?? types ? { types: opts.typeFilter ?? types } : {}),
          ...(opts.before ? { before: opts.before } : {}),
          limit: 60,
          channel,
        });
        if (seq !== requestSeq.current) return; // a newer request already won
        setCanOpenOriginal(!!res.canOpenOriginal);
        setItems((prev) => (opts.append ? [...prev, ...res.items] : res.items));
        setCursor(res.nextCursor);
      } catch (e: any) {
        if (seq === requestSeq.current) setError(e?.message || 'Could not load media.');
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [types, channel]
  );

  useEffect(() => {
    void load();
    // `load` is recreated when `types`/`channel` changes, which is exactly
    // when we want to refetch — no other dependency should trigger a reload.
  }, [load]);

  // Pull fresh usage on ARRIVAL only. Deliberately not in the effect above,
  // which also re-runs on every type-filter change.
  useEffect(() => {
    refreshUsage();
  }, []);

  // ── Live meter ────────────────────────────────────────────────────────────
  // The counter moves from things this page cannot see: a customer sending a
  // file (inbound webhook → media worker), or a delete performed in another
  // tab. `media:quota` carries the authoritative figures, so patch the shared
  // usage cache directly — a state update in the same tick, no refetch — and
  // reload the grid when the over-quota gate flips, because rows the server was
  // withholding are now returnable.
  const { user, tenant } = useAuth();
  const wsTenantId = user?.tenantId || tenant?.id;
  // Same two-axis gate ChatLogs.tsx uses for its own Instagram surfaces: the
  // tenant's PLAN must include the module AND this role must be granted it —
  // a tenant without Instagram enabled (or a role scoped away from it) never
  // sees the toggle at all, rather than seeing it and getting an empty/403 page.
  const { hasModuleAccess } = useModuleAccess();
  const { hasPermission } = usePermissionAccess();
  const instagramAvailable = hasModuleAccess('instagram') && hasPermission('instagram');
  const { usage } = useUsageLimits();
  const wasOverQuota = useRef<boolean | null>(null);

  useWebSocket(wsTenantId, {
    onMediaQuota: (data: MediaQuotaEvent) => {
      patchUsage('conversationMedia', {
        used: data.used,
        limit: data.limit,
        remaining: data.limit > 0 ? Math.max(0, data.limit - data.used) : 0,
        exceeded: data.overQuota,
        usagePercent: data.limit > 0 ? Math.min(100, (data.used / data.limit) * 100) : 0,
        unbounded: data.limit <= 0,
        skippedBytes: data.skippedBytes,
        skippedCount: data.skippedCount,
      });

      // Only reload on a TRANSITION. A capture that merely moves the number
      // does not change which rows the server will return, and reloading a
      // 60-tile grid on every inbound file would be gratuitous.
      const previous = wasOverQuota.current;
      wasOverQuota.current = data.overQuota;
      if (previous !== null && previous !== data.overQuota) void load();
    },
  });

  const mediaUsage = usage?.conversationMedia;
  const skippedBytes = mediaUsage?.skippedBytes || 0;
  const skippedCount = mediaUsage?.skippedCount || 0;

  const selectedBytes = useMemo(
    () =>
      items
        .filter((i) => selected.has(`${i.messageId}:${i.attachmentIndex}`))
        .reduce((sum, i) => sum + (i.size || i.skippedBytes || 0), 0),
    [items, selected]
  );

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const openSweep = async () => {
    setSweepOpen(true);
    setSweepPreview(null);
    try {
      const p = await adminApi.previewInboundMediaSweep(sweepDays, channel);
      setSweepPreview({ count: p.count, bytes: p.bytes });
    } catch {
      setSweepPreview({ count: 0, bytes: 0 });
    }
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      await adminApi.sweepInboundMedia(sweepDays, channel);
      setSweepOpen(false);
      setSelected(new Set());
      // Freed bytes must show in the header chip, not just the grid.
      refreshUsage();
      await load();
    } catch (e: any) {
      setError(e?.message || 'Sweep failed.');
    } finally {
      setSweeping(false);
    }
  };

  const deleteSelected = async () => {
    const keys = Array.from(selected);
    setSelected(new Set());
    try {
      // Sequential on purpose: each is a distinct destructive action, and a
      // burst of parallel deletes buys nothing at this scale.
      for (const k of keys) {
        const [messageId, idx] = k.split(':');
        await adminApi.deleteInboundMedia(messageId!, Number(idx), channel);
      }
      refreshUsage();
      await load();
    } catch (e: any) {
      setError(e?.message || 'Delete failed.');
      // A partially-completed batch still freed bytes.
      refreshUsage();
      await load();
    }
  };

  return (
    <Box>
      <PageHeader
        title="Incoming media"
        subtitle={
          channel === 'instagram'
            ? 'Files customers have sent to your Instagram account.'
            : 'Files customers have sent to your WhatsApp number.'
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Same shared usage cache the Sources pages use, so this adds no
                extra request. `conversationMedia` is the inbound pool — distinct
                from `storage`, which meters admin uploads. */}
            <UsageChip resource="conversationMedia" label="Storage" format="bytes" />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={() => {
                // Usage climbs from INBOUND webhooks, which no client action
                // observes — so the meter must be re-pulled explicitly here,
                // not just the grid. Without this, Refresh appears to do
                // nothing while the storage figure sits stale.
                refreshUsage();
                void load();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button variant="outlined" size="small" color="error" onClick={openSweep}>
              Clean up
            </Button>
          </Stack>
        }
      />

      {skippedCount > 0 && (
        /* The missing half of the meter.
         *
         * `Storage` counts bytes we are HOLDING. A file that did not fit is
         * never stored, so it moves that number by nothing — which is why a
         * chat bubble can say "storage full" while the chip still shows room
         * left: the file that bounced was bigger than the room left. This is
         * the number that explains the gap, and it is also the recovery queue:
         * everything counted here is re-fetched automatically once there is
         * space, for as long as WhatsApp still has it (7 days from receipt). */
        <Card
          variant="outlined"
          sx={(theme) => ({
            p: 1.75,
            mb: 2,
            borderRadius: 2,
            borderColor: 'warning.main',
            bgcolor: alpha(theme.palette.warning.main, 0.06),
          })}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <CloudOffOutlined fontSize="small" color="warning" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {skippedCount} file{skippedCount === 1 ? '' : 's'} · {formatBytes(skippedBytes)} not
              stored
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These arrived while storage was full, so they were never saved — that is why the
              meter did not move. Free up space or raise the allowance and they are re-fetched
              automatically
              {channel === 'instagram'
                ? '.'
                : ', for as long as WhatsApp still holds them (7 days from receipt).'}
            </Typography>
          </Stack>
        </Card>
      )}

      <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          {instagramAvailable && (
            <TextField
              select
              size="small"
              label="Channel"
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value as 'whatsapp' | 'instagram');
                setSelected(new Set());
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
            </TextField>
          )}

          <TextField
            select
            size="small"
            label="Type"
            value={types}
            onChange={(e) => {
              setTypes(e.target.value);
              setSelected(new Set());
            }}
            sx={{ minWidth: 180 }}
          >
            {TYPE_FILTERS.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flex: 1 }} />

          {selected.size > 0 && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selected.size} selected · {formatBytes(selectedBytes)}
              </Typography>
              <Button
                size="small"
                color="error"
                variant="contained"
                disableElevation
                startIcon={<DeleteOutline />}
                onClick={() => void deleteSelected()}
              >
                Delete
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<PermMedia sx={{ fontSize: 40 }} />}
          title="No media yet"
          description="Photos, voice notes and documents customers send will appear here."
        />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(6, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {items.map((item) => {
              const key = `${item.messageId}:${item.attachmentIndex}`;
              const isSelected = selected.has(key);
              const unavailable = unavailableLabel(item.storageState, item.error);
              // Same gate the "Open original" action has always used: no URL
              // (expired, refused, not yet downloaded) or no permission means
              // no preview either.
              const canPreview = !!item.s3Url && canOpenOriginal;
              const previewLabel =
                item.customerName || item.customerPhone || item.messageType || 'attachment';
              return (
                <Card
                  key={key}
                  variant="outlined"
                  sx={(theme) => ({
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    borderColor: isSelected ? 'primary.main' : undefined,
                    boxShadow: isSelected
                      ? `0 0 0 1px ${theme.palette.primary.main}`
                      : 'none',
                    transition: 'border-color .15s, box-shadow .15s',
                    '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5) },
                  })}
                >
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => toggle(key)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      left: 2,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      p: 0.5,
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  />

                  <Box
                    sx={(theme) => ({
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      position: 'relative',
                    })}
                  >
                    {item.thumbUrl ? (
                      <Box
                        component="img"
                        src={item.thumbUrl}
                        alt={item.messageType || 'attachment'}
                        loading="lazy"
                        {...(canPreview
                          ? {
                              role: 'button',
                              tabIndex: 0,
                              'aria-label': `Preview ${previewLabel}`,
                              onClick: () => setPreview(item),
                              onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setPreview(item);
                                }
                              },
                            }
                          : {})}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          ...(canPreview ? { cursor: 'pointer' } : {}),
                        }}
                      />
                    ) : (
                      <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.disabled' }}>
                        {typeIcon(item.messageType)}
                        {unavailable && (
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                            {unavailable}
                          </Typography>
                        )}
                      </Stack>
                    )}

                    {canPreview && (
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => setPreview(item)}
                          aria-label={`Preview ${previewLabel}`}
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'background.paper' },
                          }}
                        >
                          <OpenInNew sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ p: 1.25 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                      {item.customerName || item.customerPhone || 'Unknown contact'}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ mt: 0.25, color: 'text.secondary' }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>
                        {formatDate(item.createdAt)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>
                        ·
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {/* A refused capture holds no bytes, so `size` is 0 —
                            show what it WOULD have taken, which is the number
                            that explains why it bounced. */}
                        {formatBytes(item.size || item.skippedBytes || 0)}
                      </Typography>
                    </Stack>
                    {(item.promoted || item.legalHold) && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                        {item.promoted && (
                          <Chip
                            size="small"
                            label="In library"
                            sx={{ height: 18, fontSize: '0.62rem' }}
                          />
                        )}
                        {item.legalHold && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Legal hold"
                            sx={{ height: 18, fontSize: '0.62rem' }}
                          />
                        )}
                      </Stack>
                    )}
                  </Box>
                </Card>
              );
            })}
          </Box>

          {cursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => void load({ append: true, before: cursor })}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={sweepOpen} onClose={() => setSweepOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clean up old media</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently deletes stored files past their retention window. Files saved to your
            media library, or on legal hold, are never removed.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Delete media older than"
            value={sweepDays}
            onChange={async (e) => {
              const d = Number(e.target.value);
              setSweepDays(d);
              setSweepPreview(null);
              try {
                const p = await adminApi.previewInboundMediaSweep(d, channel);
                setSweepPreview({ count: p.count, bytes: p.bytes });
              } catch {
                setSweepPreview({ count: 0, bytes: 0 });
              }
            }}
          >
            {RETENTION_WINDOWS.map((d) => (
              <MenuItem key={d} value={d}>
                {d} days
              </MenuItem>
            ))}
          </TextField>

          <Divider sx={{ my: 2 }} />

          {sweepPreview === null ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">
                Checking…
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {sweepPreview.count}
              </Box>{' '}
              file{sweepPreview.count === 1 ? '' : 's'} ·{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formatBytes(sweepPreview.bytes)}
              </Box>{' '}
              will be freed.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSweepOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={sweeping || !sweepPreview || sweepPreview.count === 0}
            onClick={() => void runSweep()}
          >
            {sweeping ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <MediaPreviewDialog
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.s3Url ?? null}
        fileName={
          preview
            ? `${preview.customerName || preview.customerPhone || 'attachment'} · ${formatDate(preview.createdAt)}`
            : ''
        }
        {...(preview && previewKind(preview.messageType)
          ? { kind: previewKind(preview.messageType) as MediaPreviewKind }
          : {})}
        {...(preview?.mimeType ? { mimeType: preview.mimeType } : {})}
      />
    </Box>
  );
}
